// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// Applying a complete configuration package (the delivery zip) to the
// caller's tenant: the record the platform keeps in the task row's
// annotations.application, read the way step-cli's apply-configuration-package
// reads it, and the rule for when an application may be submitted.
import { IPermissions } from "@/types/keycloak";
/** The sections in the order the platform applies them. */
export var PACKAGE_SECTIONS = [
    "tenant-configuration",
    "tenant-settings",
    "role-permissions",
    "admin-accounts",
    "trustees",
    "election-event",
];
/** What a complete delivery carries: every section and its census. */
export var COMPLETE_DELIVERY = {
    sections: __spreadArray([], PACKAGE_SECTIONS, true),
    census: "required",
};
/** Every permission the platform's route requires, for preview as for apply. */
export var PACKAGE_PERMISSIONS = [
    IPermissions.TENANT_WRITE,
    IPermissions.USER_WRITE,
    IPermissions.ROLE_WRITE,
    IPermissions.TRUSTEE_CREATE,
    IPermissions.TRUSTEE_WRITE,
    IPermissions.ELECTION_EVENT_CREATE,
];
/** Whether the caller holds all of them on the tenant: one missing hides the page. */
export var mayApplyPackages = function (tenantId, isAuthorized) {
    return !!tenantId &&
        PACKAGE_PERMISSIONS.every(function (permission) { return isAuthorized(false, tenantId, permission); });
};
var asObject = function (value) {
    return value && typeof value === "object" ? value : {};
};
/** The record in a task row's annotations, or null while the task has none yet. */
export var readApplication = function (annotations) {
    var record = asObject(asObject(annotations).application);
    if (!record.outcome) {
        return null;
    }
    var sections = (Array.isArray(record.sections) ? record.sections : []).map(function (raw) {
        var _a, _b, _c, _d;
        var section = asObject(raw);
        var status = asObject(section.status);
        var preview = status.preview ? asObject(status.preview) : undefined;
        var submission = asObject(status.submission);
        var history = asObject(section.history);
        return {
            section: String((_a = section.section) !== null && _a !== void 0 ? _a : "?"),
            members: Array.isArray(section.members) ? section.members.map(String) : [],
            state: String((_b = status.state) !== null && _b !== void 0 ? _b : "?"),
            preview: preview && {
                needsApply: preview.needsApply === true,
                changes: preview.changes,
                blockers: Array.isArray(preview.blockers) ? preview.blockers.map(String) : [],
            },
            // SectionSubmission is camelCase (taskId); the fields inside a
            // SectionStatus variant keep their Rust name (task_id).
            taskId: (_d = (_c = submission.taskId) !== null && _c !== void 0 ? _c : status.task_id) !== null && _d !== void 0 ? _d : undefined,
            reconciled: typeof status.reconciled === "boolean" ? status.reconciled : undefined,
            error: typeof status.error === "string" ? status.error : undefined,
            earlierApplied: history.applied === true,
            submissions: (Array.isArray(history.submissions) ? history.submissions : [])
                .map(function (entry) { return asObject(entry).taskId; })
                .filter(function (id) { return typeof id === "string"; }),
            inFlight: typeof history.inFlight === "string" ? history.inFlight : undefined,
        };
    });
    var members = (Array.isArray(asObject(record.coverage).members) ? record.coverage.members : []).map(function (raw) {
        var _a, _b, _c;
        var member = asObject(raw);
        var disposition = asObject(member.disposition);
        return {
            container: typeof member.container === "string" ? member.container : undefined,
            path: String((_a = member.path) !== null && _a !== void 0 ? _a : "?"),
            disposition: String((_c = (_b = disposition.kind) !== null && _b !== void 0 ? _b : member.disposition) !== null && _c !== void 0 ? _c : "?"),
            section: disposition.section ? String(disposition.section) : undefined,
        };
    });
    return {
        outcome: record.outcome,
        packageSha256: record.packageSha256,
        mode: record.mode,
        members: members,
        sections: sections,
        blockers: sections.flatMap(function (s) { var _a, _b; return ((_b = (_a = s.preview) === null || _a === void 0 ? void 0 : _a.blockers) !== null && _b !== void 0 ? _b : []).map(function (b) { return "".concat(s.section, ": ").concat(b); }); }),
    };
};
/** How many sections a preview would change. */
export var sectionsToChange = function (view) {
    return view.sections.filter(function (s) { var _a; return (_a = s.preview) === null || _a === void 0 ? void 0 : _a.needsApply; }).length;
};
var sameRequirements = function (a, b) {
    return a.census === b.census &&
        a.sections.length === b.sections.length &&
        a.sections.every(function (section) { return b.sections.includes(section); });
};
/**
 * Whether the previewed package is the one whose SHA-256 the operator entered.
 * No entered digest is no check; the record's packageSha256 is the digest of
 * the bytes the platform read.
 */
export var shaMatches = function (view, expectedSha) { var _a; return !expectedSha || ((_a = view.packageSha256) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === expectedSha.toLowerCase(); };
/**
 * An application is offered only for the document and requirements that were
 * just previewed, when that preview finished with nothing blocking it and, if
 * the operator entered a SHA-256, the platform read exactly that package.
 */
export var mayApply = function (previewed, tenantId, documentId, requirements, expectedSha) {
    return !!previewed &&
        previewed.tenantId === tenantId &&
        previewed.documentId === documentId &&
        sameRequirements(previewed.requirements, requirements) &&
        previewed.view.outcome === "previewed" &&
        previewed.view.blockers.length === 0 &&
        shaMatches(previewed.view, expectedSha);
};
/** Whether a task's status is final: it will not change again. */
export var isFinished = function (executionStatus) {
    return executionStatus === "SUCCESS" || executionStatus === "FAILED" || executionStatus === "CANCELLED";
};
/**
 * The last line of a task row's log. A task that stops before it saves a
 * record (rejected signature, a missing document, another application on the
 * tenant) leaves its reason only there, with annotations `{document_id: null}`.
 */
export var lastLogLine = function (logs) {
    var entries = Array.isArray(logs) ? logs : [];
    var last = asObject(entries[entries.length - 1]);
    return typeof last.log_text === "string" ? last.log_text : undefined;
};
var storageKey = function (tenantId) { return "configurationPackage.active.".concat(tenantId); };
/** Remember the submission for this tenant in this browser session. */
export var rememberSubmission = function (tenantId, submission) {
    try {
        sessionStorage.setItem(storageKey(tenantId), JSON.stringify(submission));
    }
    catch (_a) {
        // Without storage the page still works; a reload just loses the task.
    }
};
/** The submission this tenant's page was following before a reload, if any. */
export var recalledSubmission = function (tenantId) {
    if (!tenantId) {
        return null;
    }
    try {
        var raw = sessionStorage.getItem(storageKey(tenantId));
        var value = raw ? JSON.parse(raw) : null;
        return value && typeof value.taskId === "string" && typeof value.documentId === "string"
            ? value
            : null;
    }
    catch (_a) {
        return null;
    }
};
/** Stop following this tenant's submission: nothing is cancelled or resubmitted. */
export var forgetSubmission = function (tenantId) {
    try {
        sessionStorage.removeItem(storageKey(tenantId));
    }
    catch (_a) {
        // Nothing stored, nothing to forget.
    }
};
var names = function (value) {
    return Array.isArray(value) ? value.filter(function (v) { return typeof v === "string"; }) : [];
};
/**
 * One readable line per proposed change of a section's preview: what it
 * names (a field, group, username, trustee or event), the action, and the
 * keys, roles or files involved. Built from an allow-list, so a value the
 * platform did not mean as a name (a setting's value, a record) is never
 * shown; a display name's old and new text are the only values shown.
 */
export var changeLines = function (changes) {
    var entries = Array.isArray(changes) ? changes : changes ? [changes] : [];
    return entries.map(function (raw) {
        var _a, _b;
        var change = asObject(raw);
        var subject = ["field", "username", "group", "name", "electionEventId"]
            .map(function (key) { return change[key]; })
            .filter(function (v) { return typeof v === "string"; })
            .join(" / ");
        var parts = [];
        if (subject)
            parts.push(subject);
        if (typeof change.action === "string")
            parts.push(change.action);
        var listed = [
            ["keys", names(change.keys)],
            ["add", names(change.add)],
            ["remove", names(change.remove)],
            ["files", names(change.files)],
        ];
        for (var _i = 0, listed_1 = listed; _i < listed_1.length; _i++) {
            var _c = listed_1[_i], label = _c[0], values = _c[1];
            if (values.length)
                parts.push("".concat(label, ": ").concat(values.join(", ")));
        }
        if (typeof change.censusSize === "number")
            parts.push("census: ".concat(change.censusSize));
        if (change.field === "displayName" &&
            (typeof change.to === "string" || change.to === null)) {
            parts.push("".concat(String((_a = change.from) !== null && _a !== void 0 ? _a : "—"), " \u2192 ").concat(String((_b = change.to) !== null && _b !== void 0 ? _b : "—")));
        }
        return parts.join(" · ") || "—";
    });
};
var SECRET_LIKE = /[A-Za-z0-9+/=_-]{32,}/g;
var MAX_TEXT = 300;
/**
 * Text from the platform, safe to show: long token-like runs (keys, hashes,
 * secrets) redacted, and the length bounded so a raw record is never echoed.
 */
export var safeText = function (text) {
    var redacted = text.replace(SECRET_LIKE, "[redacted]");
    return redacted.length > MAX_TEXT ? "".concat(redacted.slice(0, MAX_TEXT), "\u2026") : redacted;
};
